import { apiService } from './api'
import { requestCache } from '@/utils/requestCache'

export interface User {
  id: string
  email: string
  full_name: string
  first_name?: string
  last_name?: string
  role: 'super_admin' | 'admin' | 'manager' | 'sales_rep' | 'site_surveyor' | 'user'
  tenant_id?: string
  tenant_name?: string
  phone?: string
  avatar_url?: string
  status: 'active' | 'inactive' | 'suspended'
  email_verified: boolean
  last_login?: string
  created_at: string
  updated_at: string
  permissions?: string[]
  metadata?: Record<string, unknown>
}

export interface CreateUserDto extends Record<string, unknown> {
  email: string
  first_name: string
  last_name?: string
  password: string
  role: User['role']
  tenant_id?: string
  phone?: string
  permissions?: string[]
}

export interface UpdateUserDto extends Record<string, unknown> {
  first_name?: string
  last_name?: string
  role?: User['role']
  tenant_id?: string
  phone?: string
  status?: User['status']
  permissions?: string[]
}

export interface UserFilters extends Record<string, unknown> {
  tenant_id?: string
  role_id?: number  // Changed to role_id
  is_active?: boolean  // Changed from status to is_active
  search?: string
  page?: number
  size?: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  size: number
  pages: number
}

export interface ImpersonateResponse {
  token: string
  redirect_url: string
  expires_at: string
}

export interface UserStats {
  total: number
  active: number
  verified: number
  admins: number
}

class UsersService {
  // Clear users cache when data is modified
  private clearCache() {
    // Clear request cache
    requestCache.clear()
    
    // Also clear any browser cache by adding a timestamp to future requests
    console.log('[UsersService] Cache cleared after user modification')
  }
  
  async list(filters?: UserFilters): Promise<PaginatedResponse<User>> {
    try {
      // Build URL for cache key
      const params = new URLSearchParams()
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value))
          }
        })
      }
      const cacheKey = `/users?${params.toString()}`
      
      // Use cache to deduplicate requests (especially for StrictMode double-renders)
      return await requestCache.getOrFetch(
        cacheKey,
        async () => {
          console.log('[UsersService] Making API call with filters:', filters)
          const response = await apiService.users.list(filters)
          return response || { items: [], total: 0, page: 1, size: 10, pages: 0 }
        }
      )
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw new Error('Failed to fetch users')
    }
  }

  async get(userId: string): Promise<User> {
    try {
      return await apiService.users.get(userId)
    } catch (error) {
      console.error('Failed to fetch user:', error)
      throw new Error('Failed to fetch user')
    }
  }

  async stats(): Promise<UserStats> {
    try {
      return (await apiService.users.stats()) as UserStats
    } catch (error) {
      console.error('Failed to fetch user stats:', error)
      throw new Error('Failed to fetch user stats')
    }
  }

  async create(data: CreateUserDto): Promise<User> {
    try {
      // Note: apiService.users doesn't have a create method yet
      // We'll need to add it or use the generic post
      const response = await apiService.post<User>('/users', data)
      this.clearCache() // Clear cache after creating user
      return response
    } catch (error) {
      console.error('Failed to create user:', error)
      throw new Error('Failed to create user')
    }
  }

  async update(userId: string, data: UpdateUserDto): Promise<User> {
    try {
      const response = await apiService.users.update(userId, data)
      this.clearCache() // Clear cache after updating user
      return response
    } catch (error) {
      console.error('Failed to update user:', error)
      throw new Error('Failed to update user')
    }
  }

  async delete(userId: string): Promise<void> {
    try {
      await apiService.delete(`/users/${userId}`)
      this.clearCache() // Clear cache after deleting user
    } catch (error) {
      console.error('Failed to delete user:', error)
      throw new Error('Failed to delete user')
    }
  }

  async suspend(userId: string): Promise<User> {
    try {
      const response = await apiService.users.suspend(userId)
      this.clearCache() // Clear cache after suspending user
      return response
    } catch (error) {
      console.error('Failed to suspend user:', error)
      throw new Error('Failed to suspend user')
    }
  }

  async activate(userId: string): Promise<User> {
    try {
      const response = await apiService.users.activate(userId)
      this.clearCache() // Clear cache after activating user
      return response
    } catch (error) {
      console.error('Failed to activate user:', error)
      throw new Error('Failed to activate user')
    }
  }

  async resetPassword(userId: string): Promise<{ message: string }> {
    try {
      return await apiService.post(`/users/${userId}/reset-password`, {})
    } catch (error) {
      console.error('Failed to reset password:', error)
      throw new Error('Failed to reset password')
    }
  }

  async impersonate(userId: string): Promise<ImpersonateResponse> {
    try {
      return await apiService.users.impersonate(userId)
    } catch (error) {
      console.error('Failed to impersonate user:', error)
      throw new Error('Failed to impersonate user')
    }
  }

  async getPermissions(userId: string): Promise<string[]> {
    try {
      const response = await apiService.get<{data: string[]}>(`/users/${userId}/permissions`)
      return response.data || []
    } catch (error) {
      console.error('Failed to fetch user permissions:', error)
      throw new Error('Failed to fetch user permissions')
    }
  }

  async updatePermissions(userId: string, permissions: string[]): Promise<string[]> {
    try {
      const response = await apiService.put<{data: string[]}>(`/users/${userId}/permissions`, { permissions })
      return response.data
    } catch (error) {
      console.error('Failed to update user permissions:', error)
      throw new Error('Failed to update user permissions')
    }
  }

  async getUserActivity(userId: string): Promise<unknown[]> {
    try {
      const response = await apiService.get<{data: unknown[]}>(`/users/${userId}/activity`)
      return response.data || []
    } catch (error) {
      console.error('Failed to fetch user activity:', error)
      throw new Error('Failed to fetch user activity')
    }
  }

  async exportUsers(filters?: UserFilters): Promise<Blob> {
    try {
      const params = new URLSearchParams()
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value))
          }
        })
      }
      
      const response = await apiService.get<Blob>(`/users/export?${params.toString()}`, {
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
      } as Record<string, unknown>)

      return response
    } catch (error) {
      console.error('Failed to export users:', error)
      throw new Error('Failed to export users')
    }
  }
}

export const usersService = new UsersService()

// Helper functions
export function getUserRoleLabel(role: User['role']): string {
  const labels: Record<User['role'], string> = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    manager: 'Manager',
    sales_rep: 'Sales Rep',
    site_surveyor: 'Site Surveyor',
    user: 'User',
  }
  return labels[role] || role
}

export function getUserStatusLabel(status: User['status']): string {
  const labels: Record<User['status'], string> = {
    active: 'Active',
    inactive: 'Inactive',
    suspended: 'Suspended',
  }
  return labels[status] || status
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline'

export function getUserRoleColor(role: User['role']): BadgeVariant {
  const colors: Record<User['role'], BadgeVariant> = {
    super_admin: 'destructive',
    admin: 'default',
    manager: 'secondary',
    sales_rep: 'outline',
    site_surveyor: 'outline',
    user: 'outline',
  }
  return colors[role] || 'outline'
}

export function getUserStatusColor(status: User['status']): BadgeVariant {
  const colors: Record<User['status'], BadgeVariant> = {
    active: 'default',
    // 'secondary' renders green in this theme, which reads as a healthy state;
    // inactive/suspended users get neutral/destructive styling instead.
    inactive: 'outline',
    suspended: 'destructive',
  }
  return colors[status] || 'outline'
}